import React from "react";
import { useTranslation } from "react-i18next";
import { FormControl, FormControlLabel, FormLabel, Checkbox, FormGroup, Typography } from "@mui/material";
import { Question, Questionnaire } from "./models";
import RequiredAsterisk from "./RequiredAsterisk";
import { QuestionsView } from "./QuestionsView";

const MultipleChoiceMultiSelectView = ({ question, questionnaire, onChange }: {question: Question, questionnaire: Questionnaire, onChange: (Question: Question) => void}) => {
  const { t } = useTranslation(questionnaire.cohort);
  let { options, value } = question;
  options = options || [];
  value = Array.isArray(value) ? value : [];

  const handleCheckboxChange = (optionKey: string, checked: boolean) => {
    const questionValue = Array.isArray(question.value) ? question.value : [];
    const optionObj = options.find(o => o.key === optionKey);
    const isMutuallyExclusive = optionObj && optionObj.mutuallyExclusive;

    if (isMutuallyExclusive && checked) {
      // If a mutually exclusive option is checked, uncheck all others
      question.value = [optionKey];
    } else if (!isMutuallyExclusive && checked) {
      // If a non-exclusive option is checked, remove all mutually exclusive options
      const filtered = questionValue.filter(
        v => {
          const o = options.find(opt => opt.key === v);
          return !(o && o.mutuallyExclusive);
        }
      );
      question.value = [...filtered, optionKey];
    } else if (questionValue.includes(optionKey) && !checked) {
      // Uncheck the option
      question.value = questionValue.filter((x: string) => x !== optionKey);
    }

    onChange(question);
  };

  const handleOnQuestionnaireChange = (optionKey: string, followUpQuestions: Question[]) => {
    // update the follow-up questions of the selected option
    question.options = options.map(option => {
      if (option.key === optionKey) {
        option.followUpQuestions = followUpQuestions;
      }

      return option;
    });

    // update the question
    onChange(question);
  };

  // Clear the follow up questions if the value changes
  options.forEach(option => {
    if (!value.includes(option.key)) {
      option.followUpQuestions?.forEach(followUpQuestion => {
        followUpQuestion.value = followUpQuestion.defaultValue;
      });
    }
  });

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend">
        <Typography variant="h6" component="div">
          {t(question.question)}
          {question.required && <RequiredAsterisk />}
        </Typography>
      </FormLabel>
      <FormGroup>
        {options.map((option, index) => (
          <React.Fragment key={index}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value.includes(option.key)}
                  onChange={(event) => handleCheckboxChange(option.key, event.target.checked)}
                />
              }
              label={t(option.displayName)}
            />

            {value.includes(option.key) && option.followUpQuestions && (
              // If the checkbox is checked and there are follow-up questions
              <div style={{ marginLeft: '2rem' }}>
                <QuestionsView
                  questions={option.followUpQuestions}
                  questionnaire={questionnaire}
                  onChange={(followUpQuestions) => handleOnQuestionnaireChange(option.key, followUpQuestions)} />
              </div>

            )}
          </React.Fragment>
        ))}
      </FormGroup>
    </FormControl>
  );
};

export default MultipleChoiceMultiSelectView;
